import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useVersionCheck } from './use-version-check'
import { anyDirtyForm } from './dirty-form-registry'
import { forceUpdate } from './force-update'
import { logger } from '@/core/logger'
import { APP_VERSION } from './version'
import { clearUpdateAttemptIfMoved } from './update-attempt'

/**
 * React to a published version newer than the running bundle.
 *
 *  - **Forced** (`current < minRequired`): apply immediately via
 *    `forceUpdate()` — SW unregister + cache wipe + cache-busting nav. The
 *    dirty-form guard is bypassed; any in-flight draft would fail against
 *    the new schema/rules anyway.
 *  - **Optional** (`current ≥ minRequired`): dispatch the
 *    `pwa:update-available` event so the top-of-app banner shows a
 *    "Recargar" button. Held back while a form is dirty; retried on the
 *    next render (typically a navigation).
 *
 * Both apply paths converge on `forceUpdate()` — the same function the
 * banner's button calls — so the cleanup is consistent across forced and
 * optional updates. The SW's `onNeedRefresh` hook in `main.tsx` dispatches
 * the same `pwa:update-available` event, keeping the model
 * single-event-single-banner-single-apply.
 *
 * Per-session, per-`latestVersion` dedupe: once dispatched / applied for a
 * given version, the hook won't re-fire until the page reloads or a newer
 * version appears.
 *
 * History: through 1.14.47 we forced `window.location.reload()` on the
 * first navigation post-detection — robust but jarring during rapid-deploy
 * sessions (every operator click flashed the splash). 1.15.25 split into
 * optional-banner vs. forced-reload but kept both navigation-gated, so
 * idle tabs missed the banner and the forced path used a soft
 * `location.reload()` that a stale SW could still serve old HTML through.
 * Current behaviour fires on detection (no nav gate) and routes both
 * branches through `forceUpdate()` so the cleanup matches the button.
 */
export function useVersionAutoReload(): void {
  const result = useVersionCheck()
  // An update that WORKED must erase the memory of the one that did not, or
  // the next genuine release would be suppressed as "already tried".
  clearUpdateAttemptIfMoved(APP_VERSION)
  const location = useLocation()
  // Track which `latestVersion` we've already acted on this session so a
  // re-render (poll refetch, route change) doesn't re-dispatch / re-apply.
  const handledFor = useRef<string | null>(null)

  useEffect(() => {
    if (!result || !result.hasUpdate) return
    if (handledFor.current === result.latestVersion) return

    if (result.isForced) {
      handledFor.current = result.latestVersion
      logger.i(
        `version forced update: ${result.currentVersion} → ${result.latestVersion}`,
      )
      void forceUpdate()
      return
    }

    if (anyDirtyForm()) {
      logger.i('version-update dispatch skipped: dirty form in progress')
      return
    }

    handledFor.current = result.latestVersion
    logger.i(
      `version update available: ${result.currentVersion} → ${result.latestVersion}`,
    )
    window.dispatchEvent(new CustomEvent('pwa:update-available'))
    // Deps list the specific fields rather than `result` itself so a
    // TanStack-Query refetch returning a structurally equal object doesn't
    // re-fire the effect — we want a fresh run only when the verdict
    // (hasUpdate / latestVersion / isForced) actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    result?.hasUpdate,
    result?.latestVersion,
    result?.isForced,
    result?.currentVersion,
  ])
}
